import classNames from 'classnames';
import { useTranslations } from 'next-intl';

const PEOPLE = [
  {
    initials: 'LS',
    name: 'Lana Steiner',
    email: 'lana@family.com',
    roleKey: 'owner',
    avatar: 'linear-gradient(135deg,#bfc4d1,#6c727e)',
  },
  {
    initials: 'HA',
    name: 'Haleema Andrews',
    email: 'haleema@family.com',
    roleKey: 'edit',
    avatar: 'linear-gradient(135deg,#6ed7a6,#0b7b50)',
  },
  {
    initials: 'EG',
    name: 'Eden Goodwin',
    email: 'eden@family.com',
    roleKey: 'view',
    avatar: 'linear-gradient(135deg,#b69bff,#4a1ea0)',
  },
];

// Memory-keeping use case — sharing control.
export const Section33 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        {/* Share dialog */}
        <div className={styles.dialogWrap}>
          <div className={styles.dialog} aria-hidden="true">
            <div className={styles.dialogHead}>
              <span className={styles.dialogTitle}>
                {t('pages.useCases.memoryKeeping.sharing.dialogTitle')}
              </span>
              <svg viewBox="0 0 24 24" className={styles.dots}>
                <circle cx="12" cy="5" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
                <circle cx="12" cy="19" r="1.5" />
              </svg>
            </div>
            <div className={styles.addRow}>
              <span className={styles.addIcon}>
                <svg viewBox="0 0 24 24" className={styles.addGlyph}>
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </span>
              {t('pages.useCases.memoryKeeping.sharing.addPeople')}
            </div>
            {PEOPLE.map((p, i) => (
              <div
                key={p.initials}
                className={
                  i < PEOPLE.length - 1 ? styles.personDivided : styles.person
                }
              >
                <span
                  className={styles.avatar}
                  style={{ background: p.avatar }}
                >
                  {p.initials}
                </span>
                <span className={styles.personInfo}>
                  <span className={styles.personName}>{p.name}</span>
                  <span className={styles.personEmail}>{p.email}</span>
                </span>
                <span className={styles.role}>
                  {t(`pages.useCases.memoryKeeping.sharing.roles.${p.roleKey}`)}
                  {p.roleKey !== 'owner' && (
                    <svg viewBox="0 0 24 24" className={styles.chevron}>
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Copy */}
        <div className={styles.copy}>
          <div className={styles.eyebrow}>
            {t('pages.useCases.memoryKeeping.sharing.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t('pages.useCases.memoryKeeping.sharing.title')}
          </h2>
          <p className={styles.lead}>
            {t('pages.useCases.memoryKeeping.sharing.lead')}
          </p>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5',
  wrap: 'mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10',
  dialogWrap: 'flex justify-center lg:justify-start',
  dialog: classNames(
    'w-full max-w-[420px] rounded-[22px] border p-5',
    'border-[#F0F0F0] bg-white shadow-[0_14px_36px_rgba(20,30,60,0.07)]',
    'marketing-dark:border-white/10 marketing-dark:bg-[#0e1c3f]',
    'marketing-dark:shadow-[0_30px_70px_rgba(2,8,30,0.55)]',
  ),
  dialogHead: 'mb-3.5 flex items-center justify-between',
  dialogTitle:
    'text-[15px] font-bold text-mf-off-black-600 marketing-dark:text-white',
  dots: 'h-4 w-4 fill-mf-gray-400 marketing-dark:fill-white/40',
  addRow: classNames(
    'mb-3 flex items-center gap-2.5 rounded-[12px] border border-dashed px-3 py-2.5 text-[13px]',
    'border-[#DADADA] bg-white text-mf-gray-600',
    'marketing-dark:border-white/20 marketing-dark:bg-white/[0.04] marketing-dark:text-white/80',
  ),
  addIcon: classNames(
    'flex h-7 w-7 items-center justify-center rounded-full',
    'bg-[#EBF0F7] text-mf-blue',
    'marketing-dark:bg-mf-coral/20 marketing-dark:text-mf-coral',
  ),
  addGlyph: 'h-3.5 w-3.5 fill-none stroke-current stroke-2',
  person: 'flex items-center gap-3 px-2 py-2.5',
  personDivided:
    'flex items-center gap-3 border-b border-[#F0F0F0] px-2 py-2.5 marketing-dark:border-white/10',
  avatar:
    'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white',
  personInfo: 'flex-1',
  personName:
    'block text-sm font-bold text-mf-off-black-600 marketing-dark:text-white',
  personEmail: 'block text-xs text-mf-gray-500 marketing-dark:text-white/45',
  role: 'flex items-center gap-1 text-xs font-medium text-mf-gray-500 marketing-dark:text-white/55',
  chevron: 'h-3.5 w-3.5 fill-none stroke-current stroke-2',
  copy: 'text-center lg:text-left',
  eyebrow:
    'mb-4 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  lead: 'mx-auto mt-6 max-w-[480px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/72 lg:mx-0 lg:text-xl',
};
